import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';
import {ReactFlow, PanOnScrollMode, ReactFlowProvider} from 'reactflow';

import {FlowchartRenderData} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import ProcessFlowchartContext from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartContext';
import {PROCESS_FLOWCHART_EDGE_TYPE_TO_STATIC_EDGE_RENDERER} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartStaticEdgeRenderer';
import {PROCESS_FLOWCHART_NODE_TYPE_TO_TASK_NODE_RENDERER} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartTaskNodeRenderer';
import {
  DEFAULT_FIT_VIEW_OPTIONS,
  MIN_FLOWCHART_ZOOM,
  MAX_FLOWCHART_ZOOM,
  ProcessFlowchartStaticEdge,
  ProcessFlowchartStaticNode,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import useAutoFitViewOnFlowchartChange from 'processes/graph-builder-processes/flowchart-viz/useAutoFitViewOnFlowchartChange';
import {REACT_FLOW_PRO_OPTIONS} from 'ui-library/reactflow/ReactFlowConstants';

interface MinimalReadOnlyFlowchartViewerProps {
  renderData: FlowchartRenderData<ProcessFlowchartStaticNode, ProcessFlowchartStaticEdge>;
  reactFlowContainerWidth: number;
  flowchartClientId: string;
}
const lockedProps = {
  nodesConnectable: false,
  nodesDraggable: false,
  nodesFocusable: false,
  edgesFocusable: false,
  connectOnClick: false,
  selectNodesOnDrag: false,
  selectionKeyCode: null,
  zoomOnScroll: false,
  panOnScroll: true,
  panOnScrollMode: PanOnScrollMode.Vertical,
  zoomOnDoubleClick: false,
  panOnDrag: true,
};

function InnerMinimalReadOnlyFlowchartViewer({
  renderData,
}: {
  renderData: FlowchartRenderData<ProcessFlowchartStaticNode, ProcessFlowchartStaticEdge>;
}): JSX.Element {
  useAutoFitViewOnFlowchartChange(renderData);

  return (
    <div className={css(styles.flowchartWrapper)}>
      <ReactFlow
        nodes={renderData.nodes}
        edges={renderData.edges}
        fitView={true}
        minZoom={MIN_FLOWCHART_ZOOM}
        maxZoom={MAX_FLOWCHART_ZOOM}
        fitViewOptions={DEFAULT_FIT_VIEW_OPTIONS}
        nodeTypes={PROCESS_FLOWCHART_NODE_TYPE_TO_TASK_NODE_RENDERER}
        edgeTypes={PROCESS_FLOWCHART_EDGE_TYPE_TO_STATIC_EDGE_RENDERER}
        className={css(styles.flowchart)}
        proOptions={REACT_FLOW_PRO_OPTIONS}
        {...lockedProps}
      />
    </div>
  );
}

export default function MinimalReadOnlyFlowchartViewer(
  props: MinimalReadOnlyFlowchartViewerProps
): JSX.Element {
  const contextValue = useMemo(
    () => ({hideTooltips: true, flowchartClientId: props.flowchartClientId}),
    [props.flowchartClientId]
  );
  return (
    <ProcessFlowchartContext.Provider value={contextValue}>
      <ReactFlowWrapper
        width={props.reactFlowContainerWidth}
        containerWidthForControls={props.reactFlowContainerWidth}
      >
        {() => (
          <ReactFlowProvider>
            <InnerMinimalReadOnlyFlowchartViewer renderData={props.renderData} />
          </ReactFlowProvider>
        )}
      </ReactFlowWrapper>
    </ProcessFlowchartContext.Provider>
  );
}

const styles = StyleSheet.create({
  flowchartWrapper: {
    width: '100%',
    height: '100%',
  },
  flowchart: {
    overflow: 'visible',
  },
});
